import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { CustomThemeProvider } from './contexts/ThemeContext';
import { RestaurantProvider } from './contexts/RestaurantContext';
import { Box, CircularProgress, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import Login from './pages/Login';
import Layout from './components/Layout/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import HomePage from './pages/user/HomePage';
import RestaurantConfig from './components/admin/RestaurantConfig';
import { useNavigationGuard } from './hooks/useNavigationGuard';
import CheckoutPage from './pages/user/CheckoutPage';
import PaymentResultPage from './pages/user/PaymentResultPage';
import OrderSuccessPage from './pages/user/OrderSuccessPage';
import TableOrderManagement from './components/admin/TableOrderManagement';
import GuestSignupPage from './pages/user/GuestSignupPage';
import TokenManagement from './pages/TokenManagement';
import TableOrderPage from './pages/user/TableOrderPage';
import TableMenuPage from './pages/user/TableMenuPage';
import TableCheckoutPage from './pages/user/TableCheckoutPage';
import TestimonialManagement from './pages/TestimonialManagement';
import TestPage from './pages/user/TestPage';
import TermsPage from './pages/user/TermsPage';
import PrivacyPage from './pages/user/PrivacyPage';
import AboutPage from './pages/user/AboutPage';
import ContactPage from './pages/user/ContactPage';
import SocialLinksConfig from './components/admin/SocialLinksConfig';
import SkeletonLoader, { DashboardSkeleton, PageSkeleton,CheckoutPageSkeleton,MenuItemSkeleton } from './components/common/SkeletonLoader';
import OrderDetailsPage from './pages/user/OrderDetailsPage';
import ProfilePage from './components/user/ProfilePage';

// Lazy load admin components
const Dashboard = lazy(() => import('./pages/Dashboard'));
const OutletManagement = lazy(() => import('./pages/OutletManagement'));
const MenuManagement = lazy(() => import('./pages/MenuManagement'));
const DeliveryManagement = lazy(() => import('./pages/DeliveryManagement'));
const CateringManagement = lazy(() => import('./pages/CateringManagement'));

// Lazy load user pages
const DeliveryPage = lazy(() => import('./pages/user/DeliveryPage'));
const TakeawayPage = lazy(() => import('./pages/user/TakeawayPage'));
const CateringPage = lazy(() => import('./pages/user/CateringPage'));
const PublicRestaurant = lazy(() => import('./pages/PublicRestaurant'));
const TableReservationsPage = lazy(() => import('./pages/user/TableReservationsPage'));
const TableManagement = lazy(() => import('./pages/admin/TableManagement'));

// Loading component for suspense fallback
const LoadingFallback = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="100vh"
    sx={{
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
    }}
  >
    <Box textAlign="center">
      <CircularProgress size={60} sx={{ color: 'white', mb: 2 }} />
      <Typography variant="h6">Loading...</Typography>
    </Box>
  </Box>
);

// Page wrapper with Suspense
const PageWrapper = ({fallback, children }) => (
  <Suspense fallback={fallback ? fallback : <LoadingFallback />}>
    {children}
  </Suspense>
);

// Navigation guard wrapper component
const NavigationGuardWrapper = ({ children }) => {
  useNavigationGuard();
  return children;
};

function App() {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <CustomThemeProvider>
        <RestaurantProvider>
          <AuthProvider>
            <NotificationProvider>
              <Router>
                <NavigationGuardWrapper>
                  <Routes>
                    {/* User-facing routes */}
                    <Route path="/" element={<HomePage />} />
                    <Route path="/delivery" element={
                      <PageWrapper fallback={<MenuItemSkeleton/>}>
                        <DeliveryPage />
                      </PageWrapper>
                    } />
                    {/* <Route path="/takeaway" element={
                      <PageWrapper fallback={<MenuItemSkeleton/>}>
                        <takeaway />
                      </PageWrapper>
                    } /> */}
                    
                    <Route path="/catering" element={
                      <PageWrapper fallback={<MenuItemSkeleton/>}>
                        <CateringPage />
                      </PageWrapper>
                    } />

                    <Route path="/order-success" element={
                      <PageWrapper fallback={<CheckoutPageSkeleton/>}>
                        <OrderSuccessPage />
                      </PageWrapper>
                    } />

                      <Route path="/profile" element={
                      <PageWrapper fallback={<MenuItemSkeleton/>}>
                        <ProfilePage />
                      </PageWrapper>
                    } />

                    <Route path="/test" element={<TestPage />} />
                    <Route path="/table-order" element={<TableOrderPage />} />
                    <Route path="/table-order/:uniqueString" element={<TableOrderPage />} />
                    <Route path="/table-menu/:uniqueString" element={<TableMenuPage />} />
                    <Route path="/table-checkout/:uniqueString" element={
                      <PageWrapper>
                        <TableCheckoutPage />
                      </PageWrapper>
                    } />
                    <Route path="/checkout" element={
                      <PageWrapper fallback={<CheckoutPageSkeleton/>}>
                        <CheckoutPage />
                      </PageWrapper>
                    } />
                    <Route path="/guest-signup" element={
                      <PageWrapper fallback={<CheckoutPageSkeleton/>}>
                        <GuestSignupPage />
                      </PageWrapper>
                    } />
                    <Route path="/payment/success" element={
                      <PageWrapper fallback={<CheckoutPageSkeleton/>}>
                        <PaymentResultPage />
                      </PageWrapper>
                    } />
                    <Route path="/payment/failed" element={
                      <PageWrapper fallback={<CheckoutPageSkeleton/>}>
                        <PaymentResultPage />
                      </PageWrapper>
                    } />
                    <Route path="/payment/pending" element={
                      <PageWrapper fallback={<CheckoutPageSkeleton/>}>
                        <PaymentResultPage />
                      </PageWrapper>
                    } />
                    <Route path="/outlets" element={
                      <PageWrapper fallback={<MenuItemSkeleton/>}>
                        <PublicRestaurant />
                      </PageWrapper>
                    } />
                    <Route path="/table-reservations" element={
                      <PageWrapper fallback={<MenuItemSkeleton/>} >
                        <TableReservationsPage />
                      </PageWrapper>
                    } />
                    <Route path="/terms" element={
                      <PageWrapper fallback={<MenuItemSkeleton/>}>
                        <TermsPage />
                      </PageWrapper>
                    } />
                    <Route path="/privacy" element={
                      <PageWrapper fallback={<MenuItemSkeleton/>}>
                        <PrivacyPage />
                      </PageWrapper>
                    } />
                    <Route path="/about" element={
                      <PageWrapper fallback={<MenuItemSkeleton/>}>
                        <AboutPage />
                      </PageWrapper>
                    } />
                    <Route path="/contact" element={
                      <PageWrapper fallback={<MenuItemSkeleton/>}>
                        <ContactPage />
                      </PageWrapper>
                    } />

                    <Route path="/order/details/:uniqueString" element={
                      <PageWrapper fallback={<CheckoutPageSkeleton/>}>
                        <OrderDetailsPage />
                      </PageWrapper>
                    } />
                    
                    {/* Admin routes */}
                    <Route path="/admin/login" element={<Login />} />
                    <Route path="/admin" element={
                      <ProtectedRoute>
                        <Layout />
                      </ProtectedRoute>
                    }>
                      <Route index element={<Navigate to="/admin/dashboard" replace />} />
                      <Route path="dashboard" element={
                        <PageWrapper fallback={<DashboardSkeleton/>}>
                          <Dashboard />
                        </PageWrapper>
                      } />
                      <Route path="config" element={
                        <PageWrapper fallback={<DashboardSkeleton/>}>
                          <RestaurantConfig />
                        </PageWrapper>
                      } />
                      <Route path="social-links" element={
                        <PageWrapper fallback={<DashboardSkeleton/>}>
                          <SocialLinksConfig />
                        </PageWrapper>
                      } />
                      <Route path="outlets" element={
                        <PageWrapper fallback={<DashboardSkeleton/>}>
                          <OutletManagement />
                        </PageWrapper>
                      } />
                      <Route path="menu" element={
                        <PageWrapper fallback={<DashboardSkeleton/>}>
                          <MenuManagement />
                        </PageWrapper>
                      } />
                      <Route path="delivery" element={
                        <PageWrapper fallback={<DashboardSkeleton/>}>
                          <DeliveryManagement />
                        </PageWrapper>
                      } />
                      <Route path="catering" element={
                        <PageWrapper fallback={<DashboardSkeleton/>}>
                          <CateringManagement />
                        </PageWrapper>
                      } />
                      <Route path="tokens" element={
                        <PageWrapper fallback={<DashboardSkeleton/>}>
                          <TokenManagement />
                        </PageWrapper>
                      } />
                      <Route path="tables" element={
                        <PageWrapper fallback={<DashboardSkeleton/>}>
                          <TableManagement />
                        </PageWrapper>
                      } />
                      <Route path="table-orders" element={
                        <PageWrapper fallback={<DashboardSkeleton/>}>
                          <TableOrderManagement />
                        </PageWrapper>
                      } />
                       <Route path="testimonials" element={
                        <PageWrapper fallback={<PageSkeleton />}>
                          <TestimonialManagement />
                        </PageWrapper>
                      } />
                       <Route path="analytics" element={
                        <PageWrapper fallback={<SkeletonLoader />}>
                          {/* <CircularProgress /> */}
                          <PageSkeleton/> 
                          {/* // <LoadingFallback/> */}
                        </PageWrapper>
                      } />
                    </Route>
                    
                    {/* Fallback */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </NavigationGuardWrapper>
              </Router>
            </NotificationProvider>
          </AuthProvider>
        </RestaurantProvider>
      </CustomThemeProvider>
    </LocalizationProvider>
  );
}

export default App;